import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Brain, Bone, Activity, Stethoscope, Pill, Search } from "lucide-react";
import Header from "../components/marpay/Header";
import Footer from "../components/marpay/Footer";
import WhatsAppCTA from "../components/marpay/WhatsAppCTA";
import BrochureForm from "../components/marpay/BrochureForm";

const categories = [
  {
    id: "neurology",
    name: "Neurology",
    icon: Brain,
    blurb:
      "Precision-designed therapies for neuropathic pain, epilepsy and neuro-protection.",
    accent: "var(--mp-brand)",
  },
  {
    id: "psychiatry",
    name: "Psychiatry",
    icon: Activity,
    blurb:
      "Evidence-led formulations for anxiety, depression, mood and sleep — co-developed with psychiatrists.",
    accent: "var(--mp-magenta)",
  },
  {
    id: "orthopedics",
    name: "Orthopedics",
    icon: Bone,
    blurb:
      "Calcium, NSAIDs, muscle relaxants and joint-care molecules built for orthopaedic practice.",
    accent: "var(--mp-gold)",
  },
  {
    id: "general-medicine",
    name: "General Medicine",
    icon: Stethoscope,
    blurb:
      "Antibiotics, antacids, multivitamins and OTC essentials — the everyday clinical workhorses.",
    accent: "var(--mp-brand)",
  },
];

const products = [
  // Neurology
  { brand: "RFNIL", molecule: "Armodafinil 150mg", form: "Tablet", pack: "10 x 10", indication: "Neuropathic pain", category: "neurology" },
  { brand: "ATOMORTIN", molecule: "Atomotine 10/18/25mg", form: "Tablet", pack: "10 x 10", indication: "Partial onset seizures", category: "neurology" },
  { brand: "BAFLEN", molecule: "Baolofen 10mg, XL20/25/30 mg", form: "Tablet", pack: "10 x 10", indication: "Partial onset seizures", category: "neurology" },
  { brand: "MARCOBA-CD3", molecule: "Methylcobalamin IP 1500 mcg", form: "Capsule", pack: "10 x 10", indication: "Neuropathic pain / Epilepsy", category: "neurology" },
  { brand: "DULOSTAR", molecule: "Duloxetine 10/20/30mg", form: "Tablet", pack: "10 x 10", indication: "Peripheral neuropathy", category: "neurology" },
  { brand: "LORZEN", molecule: "Lorazepam 1/2mg", form: "Tablet", pack: "10 x 10", indication: "Diabetic neuropathy", category: "neurology" },
  { brand: "MARCOBA-LC", molecule: "Mecobalamin 1500mcg + L Carnitine 500mg + Folic acid 1.5 mg", form: "Tablet", pack: "10 x 10", indication: "Epilepsy / Trigeminal neuralgia", category: "neurology" },
  { brand: "Stargalin", molecule: "Peragabalin 25/50mg", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "neurology" },
  { brand: "COXIBID-TH", molecule: "Etoricoxib 60mg + Thiocolchicoside 4mg", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "neurology" },
  { brand: "ORTHOPAY ADVANCE", molecule: "Collegen Peptide TypeII 40mg, Sodium Hyluronate Sulphate 50mg, Gulcosamine Sulphate 500mg, + Vitamins", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "neurology" },
  { brand: "FLUNERTINE", molecule: "Flupirtine 100mg", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "neurology" },
  { brand: "TROXIVIT", molecule: "Lycopene 500mcg, Lutein 600mcg, Betacarotene 10mg", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "neurology" },
  { brand: "Leukocet-M", molecule: "Levocetirizine 5mg + Montelukast 10mg", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "neurology" },
  { brand: "CARNOSAFE", molecule: "L-Carnosine 200mg", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "neurology" },

  // Psychiatry
  { brand: "RFNIL", molecule: "Armodafinil 150mg", form: "Tablet", pack: "10 x 10", indication: "Neuropathic pain", category: "psychiatry" },
  { brand: "ATOMORTIN", molecule: "Atomotine 10/18/25mg", form: "Tablet", pack: "10 x 10", indication: "Partial onset seizures", category: "psychiatry" },
  { brand: "BAFLEN", molecule: "Baolofen 10mg, XL20/25/30 mg", form: "Tablet", pack: "10 x 10", indication: "Partial onset seizures", category: "psychiatry" },
  { brand: "MARCOBA-CD3", molecule: "Methylcobalamin IP 1500 mcg", form: "Capsule", pack: "10 x 10", indication: "Neuropathic pain / Epilepsy", category: "psychiatry" },
  { brand: "DULOSTAR", molecule: "Duloxetine 10/20/30mg", form: "Tablet", pack: "10 x 10", indication: "Peripheral neuropathy", category: "psychiatry" },
  { brand: "LORZEN", molecule: "Lorazepam 1/2mg", form: "Tablet", pack: "10 x 10", indication: "Diabetic neuropathy", category: "psychiatry" },
  { brand: "MARCOBA-LC", molecule: "Mecobalamin 1500mcg + L Carnitine 500mg + Folic acid 1.5 mg", form: "Tablet", pack: "10 x 10", indication: "Epilepsy / Trigeminal neuralgia", category: "psychiatry" },
  { brand: "Stargalin", molecule: "Peragabalin 25/50mg", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "psychiatry" },
  { brand: "COXIBID-TH", molecule: "Etoricoxib 60mg + Thiocolchicoside 4mg", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "psychiatry" },
  { brand: "ORTHOPAY ADVANCE", molecule: "Collegen Peptide TypeII 40mg, Sodium Hyluronate Sulphate 50mg, Gulcosamine Sulphate 500mg, + Vitamins", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "psychiatry" },
  { brand: "FLUNERTINE", molecule: "Flupirtine 100mg", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "psychiatry" },
  { brand: "TROXIVIT", molecule: "Lycopene 500mcg, Lutein 600mcg, Betacarotene 10mg", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "psychiatry" },
  { brand: "Leukocet-M", molecule: "Levocetirizine 5mg + Montelukast 10mg", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "psychiatry" },
  { brand: "CARNOSAFE", molecule: "L-Carnosine 200mg", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "psychiatry" },

  // Orthopedics
  { brand: "RFNIL", molecule: "Armodafinil 150mg", form: "Tablet", pack: "10 x 10", indication: "Neuropathic pain", category: "orthopedics" },
  { brand: "ATOMORTIN", molecule: "Atomotine 10/18/25mg", form: "Tablet", pack: "10 x 10", indication: "Partial onset seizures", category: "orthopedics" },
  { brand: "BAFLEN", molecule: "Baolofen 10mg, XL20/25/30 mg", form: "Tablet", pack: "10 x 10", indication: "Partial onset seizures", category: "orthopedics" },
  { brand: "MARCOBA-CD3", molecule: "Methylcobalamin IP 1500 mcg", form: "Capsule", pack: "10 x 10", indication: "Neuropathic pain / Epilepsy", category: "orthopedics" },
  { brand: "DULOSTAR", molecule: "Duloxetine 10/20/30mg", form: "Tablet", pack: "10 x 10", indication: "Peripheral neuropathy", category: "orthopedics" },
  { brand: "LORZEN", molecule: "Lorazepam 1/2mg", form: "Tablet", pack: "10 x 10", indication: "Diabetic neuropathy", category: "orthopedics" },
  { brand: "MARCOBA-LC", molecule: "Mecobalamin 1500mcg + L Carnitine 500mg + Folic acid 1.5 mg", form: "Tablet", pack: "10 x 10", indication: "Epilepsy / Trigeminal neuralgia", category: "orthopedics" },
  { brand: "Stargalin", molecule: "Peragabalin 25/50mg", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "orthopedics" },
  { brand: "COXIBID-TH", molecule: "Etoricoxib 60mg + Thiocolchicoside 4mg", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "orthopedics" },
  { brand: "ORTHOPAY ADVANCE", molecule: "Collegen Peptide TypeII 40mg, Sodium Hyluronate Sulphate 50mg, Gulcosamine Sulphate 500mg, + Vitamins", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "orthopedics" },
  { brand: "FLUNERTINE", molecule: "Flupirtine 100mg", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "orthopedics" },
  { brand: "TROXIVIT", molecule: "Lycopene 500mcg, Lutein 600mcg, Betacarotene 10mg", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "orthopedics" },
  { brand: "Leukocet-M", molecule: "Levocetirizine 5mg + Montelukast 10mg", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "orthopedics" },
  { brand: "CARNOSAFE", molecule: "L-Carnosine 200mg", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "orthopedics" },

  // General Medicine
  { brand: "RFNIL", molecule: "Armodafinil 150mg", form: "Tablet", pack: "10 x 10", indication: "Neuropathic pain", category: "general-medicine" },
  { brand: "ATOMORTIN", molecule: "Atomotine 10/18/25mg", form: "Tablet", pack: "10 x 10", indication: "Partial onset seizures", category: "general-medicine" },
  { brand: "BAFLEN", molecule: "Baolofen 10mg, XL20/25/30 mg", form: "Tablet", pack: "10 x 10", indication: "Partial onset seizures", category: "general-medicine" },
  { brand: "MARCOBA-CD3", molecule: "Methylcobalamin IP 1500 mcg", form: "Capsule", pack: "10 x 10", indication: "Neuropathic pain / Epilepsy", category: "general-medicine" },
  { brand: "DULOSTAR", molecule: "Duloxetine 10/20/30mg", form: "Tablet", pack: "10 x 10", indication: "Peripheral neuropathy", category: "general-medicine" },
  { brand: "LORZEN", molecule: "Lorazepam 1/2mg", form: "Tablet", pack: "10 x 10", indication: "Diabetic neuropathy", category: "general-medicine" },
  { brand: "MARCOBA-LC", molecule: "Mecobalamin 1500mcg + L Carnitine 500mg + Folic acid 1.5 mg", form: "Tablet", pack: "10 x 10", indication: "Epilepsy / Trigeminal neuralgia", category: "general-medicine" },
  { brand: "Stargalin", molecule: "Peragabalin 25/50mg", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "general-medicine" },
  { brand: "COXIBID-TH", molecule: "Etoricoxib 60mg + Thiocolchicoside 4mg", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "general-medicine" },
  { brand: "ORTHOPAY ADVANCE", molecule: "Collegen Peptide TypeII 40mg, Sodium Hyluronate Sulphate 50mg, Gulcosamine Sulphate 500mg, + Vitamins", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "general-medicine" },
  { brand: "FLUNERTINE", molecule: "Flupirtine 100mg", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "general-medicine" },
  { brand: "TROXIVIT", molecule: "Lycopene 500mcg, Lutein 600mcg, Betacarotene 10mg", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "general-medicine" },
  { brand: "Leukocet-M", molecule: "Levocetirizine 5mg + Montelukast 10mg", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "general-medicine" },
  { brand: "CARNOSAFE", molecule: "L-Carnosine 200mg", form: "Tablet", pack: "10 x 10", indication: "Migraine prophylaxis / Epilepsy", category: "general-medicine" },
];

export default function ProductsPage() {
  const [active, setActive] = useState("all");
  const [query, setQuery] = useState("");

  const counts = useMemo(() => {
    const m = { all: products.length };
    for (const c of categories) m[c.id] = products.filter((p) => p.category === c.id).length;
    return m;
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) => {
      const inCat = active === "all" || p.category === active;
      if (!inCat) return false;
      if (!q) return true;
      return (
        p.brand.toLowerCase().includes(q) ||
        p.molecule.toLowerCase().includes(q) ||
        p.indication.toLowerCase().includes(q)
      );
    });
  }, [active, query]);

  return (
    <main data-testid="products-page" className="bg-[var(--mp-bg)] text-[var(--mp-text)]">
      <Header />

      {/* Hero */}
      <section className="relative pt-36 md:pt-44 pb-16 md:pb-20 crosshair-bg overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <div className="overline mb-5">
              <span className="inline-flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--mp-brand)]" />
                Product Portfolio · 2026
              </span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-medium leading-[0.98]" data-testid="products-headline">
              Four specialties.
              <br />
              <span className="italic font-normal" style={{ color: "var(--mp-magenta)" }}>
                One disciplined portfolio.
              </span>
            </h1>
            <p className="mt-7 text-lg md:text-xl text-[var(--mp-text-2)] leading-relaxed max-w-2xl">
              Browse Marpay's active product range across Neurology, Psychiatry, Orthopedics and
              General Medicine. For a complete brochure, sample requests or PCD partnership —{" "}
              <Link to="/#contact" className="underline decoration-[var(--mp-brand)] underline-offset-4">
                get in touch
              </Link>
              .
            </p>
          </div>

          <div className="lg:col-span-4">
            <div className="rounded-2xl border border-[var(--mp-border)] bg-white p-6 shadow-sm">
              <div className="overline mb-3">Quick search</div>
              <label className="relative block">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--mp-text-2)]" />
                <input
                  data-testid="products-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search brand, molecule, indication…"
                  className="w-full pl-10 pr-3 py-3 rounded-xl border border-[var(--mp-border)] bg-[var(--mp-bg)] focus:outline-none focus:border-[var(--mp-text)] text-sm"
                />
              </label>
              <p className="mt-3 text-xs text-[var(--mp-text-2)]">
                {filtered.length} of {products.length} products shown
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Category tabs */}
      <section className="border-y border-[var(--mp-border)] bg-white sticky top-[68px] z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-2 py-4 overflow-x-auto" data-testid="category-tabs">
            <CategoryTab
              id="all"
              label="All Products"
              count={counts.all}
              active={active === "all"}
              onClick={() => setActive("all")}
            />
            {categories.map((c) => (
              <CategoryTab
                key={c.id}
                id={c.id}
                label={c.name}
                count={counts[c.id]}
                active={active === c.id}
                onClick={() => setActive(c.id)}
                accent={c.accent}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Category intro cards */}
      {active !== "all" && (
        <section className="pt-12 md:pt-16">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            {categories
              .filter((c) => c.id === active)
              .map((c) => {
                const Icon = c.icon;
                return (
                  <motion.div
                    key={c.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="rounded-3xl border border-[var(--mp-border)] bg-white p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6"
                    data-testid={`category-intro-${c.id}`}
                  >
                    <div
                      className="h-14 w-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                      style={{ background: "rgba(42,75,156,0.08)", color: c.accent }}
                    >
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <div className="overline mb-1" style={{ color: c.accent }}>{c.name}</div>
                      <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight">{c.blurb}</h3>
                    </div>
                  </motion.div>
                );
              })}
          </div>
        </section>
      )}

      {/* Products list grouped */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {active === "all"
            ? categories.map((c) => {
              const items = filtered.filter((p) => p.category === c.id);
              if (items.length === 0) return null;
              return (
                <CategorySection key={c.id} category={c} items={items} />
              );
            })
            : (
              <ProductGrid items={filtered} accent={categories.find((c) => c.id === active)?.accent || "var(--mp-brand)"} />
            )}

          {filtered.length === 0 && (
            <div className="text-center py-20 text-[var(--mp-text-2)]" data-testid="products-empty-state">
              <Pill className="h-10 w-10 mx-auto mb-4 text-[var(--mp-border)]" />
              No products found for "{query}". Try a different molecule or category.
            </div>
          )}
        </div>
      </section>

      {/* CTA strip */}
      <section className="pb-24 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="rounded-3xl p-10 md:p-14 relative overflow-hidden" style={{ background: "var(--mp-dark)" }}>
            <div aria-hidden className="absolute -top-20 -right-20 w-80 h-80 rounded-full" style={{ background: "rgba(237,177,45,0.25)", filter: "blur(50px)" }} />
            <div aria-hidden className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full" style={{ background: "rgba(175,17,130,0.25)", filter: "blur(50px)" }} />
            <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-start text-white">
              <div className="md:col-span-6">
                <div className="overline mb-3" style={{ color: "var(--mp-gold)" }}>Distributors &amp; Doctors</div>
                <h3 className="font-display text-3xl sm:text-5xl tracking-tighter font-medium leading-[1.05]">
                  Want the complete brochure
                  <br />
                  with pricing &amp; territories?
                </h3>
                <p className="mt-5 text-slate-300 leading-relaxed max-w-md">
                  Drop your details — we'll instantly generate the latest Marpay Biotec product
                  brochure as a PDF and email future updates to you.
                </p>
              </div>
              <div className="md:col-span-6">
                <BrochureForm />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppCTA />
    </main>
  );
}

function CategoryTab({ id, label, count, active, onClick, accent }) {
  return (
    <button
      onClick={onClick}
      data-testid={`category-tab-${id}`}
      className={`group inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium border transition-all duration-200 ${active
        ? "border-transparent text-white"
        : "border-[var(--mp-border)] text-[var(--mp-text-2)] hover:text-[var(--mp-text)] bg-white"
        }`}
      style={active ? { background: "var(--mp-brand)" } : undefined}
    >
      {label}
      <span
        className={`text-[10px] px-2 py-0.5 rounded-full font-mono-display ${active ? "bg-white/20 text-white" : "bg-[var(--mp-bg)] text-[var(--mp-text-2)]"
          }`}
      >
        {count}
      </span>
    </button>
  );
}

function CategorySection({ category, items }) {
  const Icon = category.icon;
  return (
    <div id={category.id} className="mb-16 last:mb-0">
      <div className="flex items-center gap-4 mb-7">
        <div
          className="h-12 w-12 rounded-2xl flex items-center justify-center"
          style={{ background: "rgba(42,75,156,0.08)", color: category.accent }}
        >
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="overline mb-1" style={{ color: category.accent }}>{category.name}</div>
          <h3 className="font-display text-2xl md:text-3xl tracking-tight font-medium">
            {category.blurb}
          </h3>
        </div>
      </div>
      <ProductGrid items={items} accent={category.accent} />
    </div>
  );
}

function ProductGrid({ items, accent }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
      {items.map((p, i) => (
        <motion.div
          key={p.brand}
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.4, delay: (i % 8) * 0.04 }}
          data-testid={`product-card-${p.brand.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className="relative rounded-2xl border border-[var(--mp-border)] bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl overflow-hidden"
        >
          <div className="absolute inset-x-0 top-0 h-1" style={{ background: accent }} />
          <div className="flex items-start justify-between mb-6">
            <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(42,75,156,0.08)", color: accent }}>
              <Pill className="h-5 w-5" strokeWidth={1.6} />
            </div>
            <span className="font-mono-display text-[10px] tracking-[0.2em] uppercase text-[var(--mp-text-2)]">
              {p.form}
            </span>
          </div>
          <h4 className="font-display text-xl font-medium tracking-tight">{p.brand}</h4>
          <p className="mt-1 text-sm text-[var(--mp-text-2)] leading-relaxed">{p.molecule}</p>
          <div className="mt-4 pt-4 border-t border-[var(--mp-border)] flex items-center justify-between text-xs">
            <span className="text-[var(--mp-text-2)]">{p.indication}</span>
            <span className="font-mono-display text-[10px] tracking-widest uppercase text-[var(--mp-text)]">
              {p.pack}
            </span>
          </div>
        </motion.div>
      ))}
    </div>
  );
}
